import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';
import MarkdownContent from '../../components/content/MarkdownContent';

/**
 * LegalDocumentManagement (SCRUM-2204 — MVP 1.40.4)
 *
 * Super-admin authoring surface for the five MVP 1.40 legal documents.
 * List + draft editor (with live preview via the shared MarkdownContent
 * renderer used on the public page) + read-only history.
 *
 * The publish endpoint server-stamps effective_date / published_at; there
 * is no effective-date input on this page. The publish confirm dialog
 * states the effective date will be set to today.
 */

const styles = {
  container: { maxWidth: '1100px', margin: '0 auto', padding: '24px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: '600', margin: 0 },
  card: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' },
  btn: { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnDanger: { backgroundColor: '#ef4444', color: '#fff' },
  input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%' },
  textarea: { padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', minHeight: '420px', resize: 'vertical' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' },
  badgeDraft: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e' },
  badgePublished: { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#d1fae5', color: '#065f46' },
  errorBox: { padding: '8px 12px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' },
  previewWrap: { padding: '12px', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: '#fafafa', minHeight: '420px', maxHeight: '600px', overflow: 'auto' },
};

const ADMIN_BASE = '/api/v1/admin/legal-documents';

function formatDate(value) {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return String(value);
  }
}

function extractApiError(err) {
  if (!err) {
    return 'Request failed';
  }
  if (err.error && typeof err.error === 'object') {
    return err.error.message || err.error.code || err.message || 'Request failed';
  }
  return err.message || (typeof err.error === 'string' ? err.error : 'Request failed');
}

export default function LegalDocumentManagement() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null); // full doc with versions[]
  const [listError, setListError] = useState(null);

  const handleNavigate = (_id, path) => { if (path) { navigate(path); } };

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await apiRequest(ADMIN_BASE);
      const list = (res && res.data) || [];
      const enriched = await Promise.all(list.map(async (d) => {
        try {
          const detail = await apiRequest(`${ADMIN_BASE}/${d.id}`);
          const versions = (detail && detail.data && detail.data.versions) || [];
          const draft = versions.find((v) => v.status === 'draft') || null;
          const current = versions.find((v) => v.id === d.current_version_id) || null;
          return { ...d, draft, current };
        } catch {
          return { ...d, draft: null, current: null };
        }
      }));
      setDocuments(enriched);
    } catch (err) {
      setListError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (docId) => {
    try {
      const res = await apiRequest(`${ADMIN_BASE}/${docId}`);
      setSelectedDoc(res.data);
    } catch (err) {
      setListError(extractApiError(err));
    }
  };

  const refreshDetail = async () => {
    if (!selectedDoc) {
      return;
    }
    const res = await apiRequest(`${ADMIN_BASE}/${selectedDoc.id}`);
    setSelectedDoc(res.data);
    await loadList();
  };

  if (selectedDoc) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="legal-documents"
        onNavigate={handleNavigate}
        user={{ name: authUser?.fullName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
      >
        <DetailView
          doc={selectedDoc}
          onBack={() => setSelectedDoc(null)}
          onChanged={refreshDetail}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="legal-documents"
      onNavigate={handleNavigate}
      user={{ name: authUser?.fullName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <h1 style={styles.title}>Legal Documents</h1>
        </div>

        {listError && <div style={styles.errorBox}>{listError}</div>}

        <div style={styles.card}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Document</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Current Version</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>Loading…</td></tr>
              ) : documents.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>No documents found.</td></tr>
              ) : (
                documents.map((d) => (
                  <tr key={d.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: '500' }}>{d.title}</div>
                      <code style={{ fontSize: '12px', color: '#6b7280' }}>{d.slug}</code>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>
                      {d.current ? `v${d.current.version_number}` : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>none yet</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {d.draft && <span style={styles.badgeDraft}>Draft in progress</span>}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        style={{ ...styles.btn, ...styles.btnSecondary }}
                        onClick={() => openDetail(d.id)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function DetailView({ doc, onBack, onChanged }) {
  const versions = doc.versions || [];
  const draft = versions.find((v) => v.status === 'draft') || null;
  const current = versions.find((v) => v.id === doc.current_version_id) || null;
  const history = versions.filter((v) => v.status === 'published');

  return (
    <div style={styles.container}>
      <div style={{ marginBottom: '12px' }}>
        <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={onBack}>← Back to list</button>
      </div>

      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>{doc.title}</h1>
          <code style={{ fontSize: '12px', color: '#6b7280' }}>{doc.slug}</code>
        </div>
        <div style={{ color: '#6b7280', fontSize: '13px' }}>
          {current ? <>Current: v{current.version_number} · Effective {formatDate(current.effective_date)}</> : <em>No version published yet</em>}
        </div>
      </div>

      <DraftEditor doc={doc} draft={draft} onChanged={onChanged} />

      <HistoryPanel history={history} currentVersionId={doc.current_version_id} />
    </div>
  );
}

function DraftEditor({ doc, draft, onChanged }) {
  const [versionNumber, setVersionNumber] = useState(draft?.version_number || '');
  const [body, setBody] = useState(draft?.body || '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    setVersionNumber(draft?.version_number || '');
    setBody(draft?.body || '');
    setError(null);
    // We intentionally re-sync inputs only when the active draft identity changes
    // (e.g. after refetch). Re-syncing on every body keystroke would clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);

  const startDraft = async () => {
    if (!versionNumber.trim()) { setError('version_number is required'); return; }
    if (!body || body.length === 0) { setError('body is required'); return; }
    setError(null);
    setBusy(true);
    try {
      await apiRequest(`${ADMIN_BASE}/${doc.id}/versions`, {
        method: 'POST',
        body: { version_number: versionNumber.trim(), body },
      });
      await onChanged();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!draft) { return; }
    if (!versionNumber.trim()) { setError('version_number is required'); return; }
    if (!body || body.length === 0) { setError('body is required'); return; }
    setError(null);
    setBusy(true);
    try {
      await apiRequest(`${ADMIN_BASE}/${doc.id}/versions/${draft.id}`, {
        method: 'PUT',
        body: { version_number: versionNumber.trim(), body },
      });
      await onChanged();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const discardDraft = async () => {
    if (!draft) { return; }
    setConfirmDiscard(false);
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`${ADMIN_BASE}/${doc.id}/versions/${draft.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    if (!draft) { return; }
    setConfirmPublish(false);
    setBusy(true);
    setError(null);
    try {
      // Save any pending edits first so the published version reflects current inputs.
      await apiRequest(`${ADMIN_BASE}/${doc.id}/versions/${draft.id}`, {
        method: 'PUT',
        body: { version_number: versionNumber.trim(), body },
      });
      await apiRequest(`${ADMIN_BASE}/${doc.id}/versions/${draft.id}/publish`, { method: 'POST' });
      await onChanged();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...styles.card, padding: '20px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
          {draft ? 'Edit Draft' : 'Start New Draft'}
        </h2>
        {draft && <span style={styles.badgeDraft}>Draft</span>}
      </div>

      {error && <div style={styles.errorBox} role="alert">{error}</div>}

      <div style={{ marginBottom: '12px' }}>
        <label style={styles.label} htmlFor="legal-doc-version-number">Version Number</label>
        <input
          id="legal-doc-version-number"
          style={styles.input}
          value={versionNumber}
          onChange={(e) => setVersionNumber(e.target.value)}
          placeholder='e.g. "1.1"'
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
        <div>
          <label style={styles.label} htmlFor="legal-doc-body">Markdown Body</label>
          <textarea
            id="legal-doc-body"
            style={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="# Title&#10;&#10;Document body in markdown…"
          />
        </div>
        <div>
          <label style={styles.label}>Live Preview</label>
          <div style={styles.previewWrap} data-testid="legal-doc-preview">
            <MarkdownContent>{body || ''}</MarkdownContent>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {!draft && (
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={startDraft} disabled={busy}>
            {busy ? 'Starting…' : 'Start Draft'}
          </button>
        )}
        {draft && (
          <>
            <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => setConfirmDiscard(true)} disabled={busy}>
              Discard Draft
            </button>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={saveDraft} disabled={busy}>
              {busy ? 'Saving…' : 'Save Draft'}
            </button>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setConfirmPublish(true)} disabled={busy}>
              Publish…
            </button>
          </>
        )}
      </div>

      {confirmPublish && (
        <Dialog title="Publish Version" onClose={() => setConfirmPublish(false)}>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#374151' }}>
            Publish v{versionNumber || draft?.version_number} of <strong>{doc.title}</strong>?
            The effective date will be set to <strong>today</strong>. Once published, the version is immutable.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setConfirmPublish(false)}>Cancel</button>
            <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={publishDraft} disabled={busy}>
              {busy ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </Dialog>
      )}

      {confirmDiscard && (
        <Dialog title="Discard Draft" onClose={() => setConfirmDiscard(false)}>
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#374151' }}>
            Discard the in-progress draft for <strong>{doc.title}</strong>? This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setConfirmDiscard(false)}>Cancel</button>
            <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={discardDraft} disabled={busy}>Discard</button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

function HistoryPanel({ history, currentVersionId }) {
  return (
    <section style={{ ...styles.card, padding: '20px' }}>
      <h2 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px' }}>Version History</h2>
      {history.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>No published versions yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Version</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Status</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '500', color: '#6b7280', fontSize: '13px' }}>Published</th>
            </tr>
          </thead>
          <tbody>
            {history.map((v) => (
              <tr key={v.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px 12px' }}>
                  v{v.version_number}
                  {v.id === currentVersionId && (
                    <span style={{ ...styles.badgePublished, marginLeft: '8px' }}>Current</span>
                  )}
                </td>
                <td style={{ padding: '8px 12px' }}>
                  <span style={styles.badgePublished}>Published</span>
                </td>
                <td style={{ padding: '8px 12px', color: '#374151' }}>{formatDate(v.published_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Dialog({ title, onClose, children }) {
  return (
    <div
      role="dialog"
      aria-label={title}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '440px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}
